import { Injectable, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Json } from '@se/cp.common';
import { isNil } from 'lodash-es';
import { Observable, catchError, from, map, mergeMap, of, switchMap } from 'rxjs';
import { ISourceItemDataExtended } from '../../models';
import { ExaminationPatientHistoryDTO, HistoryState, ReportHistoryActorErrors, ReportHistoryDTO, ReportHistoryRequestDTO, ReportPatientHistoryDTO } from '../../models/patient-history.models';
import { RootState } from '../../root-store/root.state';
import { selectHasPatientHistory } from '../../se-side/redux/se-side-nav.selectors';
import { ExaminationPatientHistoryTimeline } from '../../se-timeline/se-timeline.models';
import { ListDataSourceService } from '../list-datasource.service';
import { PatientReportHistoryBaseService } from './base/patient-report-history-base.service';

@Injectable({
    providedIn: 'root',
})

export class PatientReportHistoryService extends PatientReportHistoryBaseService {
    private store: Store<RootState> = inject(Store<RootState>);
    private translate: TranslateService = inject(TranslateService);
    private datasourceService: ListDataSourceService = inject(ListDataSourceService);

    /** SIGNALS */
    public _cpData = signal<Json | null>(null);
    private _cpSources = signal<string[]>([]);
    private _hasReport$: Observable<boolean> = this.store.pipe(
        select(selectHasPatientHistory)
    );
    public _reportHistoryArray = signal<ReportHistoryDTO[]>([]);
    private _reportHistoryErrors = signal<ReportHistoryActorErrors[]>([]);
    private _selectedModalities = signal<string[]>([]);
    private actorsList = signal<ISourceItemDataExtended[]>([]);
    private alreadyLoaded = signal<boolean>(false);
    private completedActors = signal<Set<string>>(new Set());
    private errorActors = signal<Set<string>>(new Set());
    private lastStatus = signal<'none' | 'partial' | 'error'>('none');

    /** SIGNALS R-O */
    readonly cpData = this._cpData.asReadonly();
    readonly cpSources = this._cpSources.asReadonly();
    readonly hasReport: Signal<boolean | undefined> = toSignal(this._hasReport$, { initialValue: false });
    readonly reportHistoryArray = this._reportHistoryArray.asReadonly();
    readonly reportHistoryErrors = this._reportHistoryErrors.asReadonly();
    readonly selectedModalities = this._selectedModalities.asReadonly();

    /** COMPUTED: costruisce i payload per tutte le sources */
    readonly reportHistoryPayloads = computed<ReportHistoryRequestDTO[] | undefined>(() => {
        const data: any = this._cpData();
        const sources: string[] = this.cpSources();
        const active: boolean = this.hasReport();

        if (!active || !data || isNil(data) || !sources.length) return [];

        return sources.map((source: string) => ({
            idPatient: data.Patient?.Id ?? '',
            patientId: data.Patient?.PatientId ?? null,
            issuer: data.Patient?.Issuer ?? null,
            idSource: source,
        }));
    });

    // Computed con tutte le examinations aggregate e mappate nel tipo che serve alla Timeline
    readonly allExaminations = computed<ExaminationPatientHistoryTimeline[]>(() => {
        return this._reportHistoryArray()
            .flatMap((reportHistory: ReportHistoryDTO) =>
                reportHistory.reports.flatMap((report: ReportPatientHistoryDTO) =>
                    report.examinations.map((exam: ExaminationPatientHistoryDTO) => ({
                        ...exam,
                        reportDate: report.reportDate,
                        reportId: report.id,
                        description: exam.exam,
                    }))
                )
            );
    });

    readonly filteredExaminationsByModalities = computed<ExaminationPatientHistoryTimeline[]>(() => {
        const selectedModalities = this._selectedModalities();

        return this.allExaminations().filter((exam: ExaminationPatientHistoryTimeline) => {
            // Se non c'è alcuna selezione mostro tutti
            if (!selectedModalities.length) return true;
            // altrimenti mostro solo quelli con modalità selezionate
            return selectedModalities.includes(exam.modality!);
        });
    });

    readonly modalitiesListComputed = computed<string[]>(() => {
        const patientHistory: ReportHistoryDTO[] = this.reportHistoryArray();
        let modalities: string[] = [];
        patientHistory.forEach(history => {
            history.reports.forEach(report => {
                report.examinations.forEach(exam => {
                    if (exam.modality) {
                        modalities.push(exam.modality);
                    }
                });
            });
        });

        let modalitiesNoDuplicate: string[] = [...new Set(modalities)];
        return modalitiesNoDuplicate;
    });

    /**
     * computed per i contatori di successo/errore/completati delle chiamate ai vari attori
     */
    totalActors = computed(() =>
        this.reportHistoryPayloads()?.length ?? 0
    );

    successCount = computed(() =>
        this.reportHistoryArray().length
    );

    errorCount = computed(() =>
        this.reportHistoryErrors().length
    );

    /**
     * solo alcuni vanno a buon fine
     */
    isPartialSuccess = computed(() => {
        const total = this.totalActors();
        const completed = this.completedActors().size;
        const errors = this.errorActors().size;

        return (
            total > 0 &&
            completed === total &&
            errors > 0 &&
            errors < total
        );
    });

    /**
     * tutti gli attori danno errore
     */
    isTotalFailure = computed(() => {
        const total = this.totalActors();
        return total > 0 && this.errorActors().size === total;
    });

    readonly reportHistoryState = computed<HistoryState>(() => {
        if (this.isTotalFailure()) return HistoryState.ERROR;
        if (this.isPartialSuccess()) return HistoryState.INCOMPLETE;
        if (this.reportHistoryArray().length === 0) return HistoryState.SUCCESS_NO_RESULT;
        return HistoryState.SUCCESS;
    });

    readonly headerLoading = computed(() => {
        const total = this.reportHistoryPayloads()?.length ?? 0;
        const completed = this.completedActors().size;

        if (total === 0) return false;

        return completed < total;
    });
    constructor() {
        super();

        /** EFFECT --> lazyLoading delle chiamate http (una per ogni source della CP)
         * aggiorna il valore di _reportHistoryArray() ogni volta che arriva una risposta
         * con switchMap cancello tutte le chiamate già avviate nel caso in cui il payload cambi
         * con untracked permette di modificare il computed senza che angular lo valorizzi come dipendenza (e quindi evita loop)
         * */
        effect(() => {
            const payloads = this.reportHistoryPayloads();
            if (!payloads) return;
            // Se non ci sono payloads, resetto e non faccio nulla
            if (payloads.length === 0) {
                this._reportHistoryArray.set([]);
                this._reportHistoryErrors.set([]);
                this.completedActors.set(new Set());
                return;
            }
            untracked(() => {
                this._reportHistoryArray.set([]);
                this._reportHistoryErrors.set([]);
                this.completedActors.set(new Set());
                // usando payload (array) si esegue una sola volta e non N volte
                from([payloads])
                    .pipe(
                        // Ogni volta che cambia il set di payloads, cancella le chiamate precedenti
                        switchMap((payloadsArray) =>
                            from(payloadsArray).pipe(
                                // Faccio tutte le chiamate in parallelo
                                mergeMap(payload => this.getReportHistory(payload).pipe(
                                    map(response => ({
                                        type: 'success' as const,
                                        payload,
                                        response
                                    })),
                                    catchError(error => of({
                                        type: 'error' as const,
                                        payload,
                                        error
                                    }))
                                )
                            ))
                        )
                    )
                    .subscribe(result => {
                        const completed = new Set(this.completedActors());
                        completed.add(result.payload.idSource!);
                        this.completedActors.set(completed);

                        if (result.type === 'success') {
                             // Aggiungo la risposta appena arriva (aggiungo allo stesso array --> il DOM non si ridisegna perchè c'è track)
                                    const current = this._reportHistoryArray();
                                    // Evito duplicati confrontando la lista di report tramite id
                                    const isDuplicate = current.some((existing: ReportHistoryDTO) =>
                                        existing.reports.some((report: ReportPatientHistoryDTO) =>
                                            result.response.reports.some(newReport => newReport.id === report.id)
                                        )
                                    );
                                    if (!isDuplicate) {
                                        this._reportHistoryArray.set([...current, result.response]);
                                    }
                        }
                        if (result.type === 'error') {
                            const errors = new Set(this.errorActors());
                            errors.add(result.payload.idSource!);
                            this.errorActors.set(errors);

                            const currentErrors = this._reportHistoryErrors();

                            this._reportHistoryErrors.set([
                                ...currentErrors,
                                {
                                    actorDetail: this.actorsList().find(actor => actor.guid === result.payload.idSource) ?? result.payload.idSource,
                                    error: result.error
                                }
                            ]);
                        }
                    })
            });
        });

        /**
         * EFFECT --> carica la lista delle sorgenti disponibili per l'history
         * solo quando ci sono delle sorgenti nella CP e non sono già state caricate
         * (serve per mappare l'id che arriva dalle CP con la descrizione da mostrare negli errori)
        */
        effect(() => {
            const actorIds = this.cpSources();
            if (!actorIds.length || this.alreadyLoaded()) return;

            this.datasourceService
                .getPatientHistorySources()
                .then(actorsList => {
                    if (actorsList.length) {
                        this.alreadyLoaded.set(true);
                        this.actorsList.set(actorsList);
                    }
                });
        });
    }

    setCpData(data: Json) {
        /** ogni volta che carico nuovi dati (quindi cambio prestazione)
        * resetto tutto lo store ai valori di default
        */
        this.resetData();
        this._cpData.set(data);
    }

    setCpSources(sources: string[]) {
        this._cpSources.set(sources);
    }

    setSelectedModalities(modalities: string[]) {
        this._selectedModalities.set(modalities);
    }

    resetData() {
        this._cpData.set(null);
        this._cpSources.set([]);
        this._reportHistoryArray.set([]);
        this._reportHistoryErrors.set([]);
        this._selectedModalities.set([]);
        this.lastStatus.set('none');
    }
}
